import { useState, useEffect } from "react";
import { CustomProperty, UpdateCustomPropertySettingsContract } from "winslow-core";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import { DropdownSettings } from "./customPropertyConfig";
import { X } from "lucide-react";
import { apiUpdateCustomPropertySettings } from "../../../services/EmployeeRecordService";
import toast from "react-hot-toast";
import { toastFail } from "../../../config";
import { AppCustomPropertyError } from "./AppCustomPropertyError";

type AppCustomPropertyDropdownProps = {
    customProperty: CustomProperty;
    data: any;
    disabled: boolean;
    validate: boolean;
    onChange: (label: string, data: any) => void;
}

export const AppCustomPropertyDropdown = ({customProperty, data, disabled, validate, onChange} : AppCustomPropertyDropdownProps) => {

    const [value, setValue] = useState<string | undefined>(data as string || undefined);
    const [selected, setSelected] = useState<GenericSelectOption | undefined>(undefined);
    const [options, setOptions] = useState<GenericSelectOption[]>([]);
    
    const [showNewChoice, setShowNewChoice] = useState<boolean>(false);
    const settings = JSON.parse(customProperty.settings || "{}") as DropdownSettings;

    useEffect(() => {
        const theOptions: GenericSelectOption[] = settings.choices ? settings.choices.map((choice: string) => {
            return (
                {
                    displayName: choice,
                    value: choice,
                    image: undefined,
                    component: undefined,
                } as GenericSelectOption
            )
        }) : [];

        // do we have to sort
        if (settings.alphabetize) {
            theOptions.sort((a, b) => { return a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()) })
        }

        if (!customProperty.required) {
            theOptions.unshift({
                displayName: "(none)",
                value: "",
                image: undefined,
                component: undefined,
            } as GenericSelectOption);
        }
        
        if (settings.editable) {
            theOptions.push({
                displayName: "+ Add Choice",
                value: "__new__",
                image: undefined,
                component: undefined,
            } as GenericSelectOption);
        }

        setOptions(theOptions);
        const theSelected = (data || settings.default) ? theOptions.find((option) => option.value === (data || settings.default)) : theOptions[0];
        setSelected(theSelected ? {...theSelected} : undefined);

        // document.addEventListener("keydown", eventKeydDwnHandler, true);
        // return () => {
        //     document.removeEventListener("keydown", eventKeydDwnHandler, true);
        // }
    }, []);

    // useEffect(() => {
    //     if (data) {
    //         setValue(data);
    //         setSelected(options.find((option) => option.value === data));
    //     }
    // }, [data]);

    useEffect(() => {
        if (value !== undefined) {
            onChange(customProperty.label, value);
            setSelected(options.find((option) => option.value === value));
        }
    }, [value]);

    const handleAddChoice = async (value: string) => {
        const newOption = {
            displayName: value,
            value: value,
            image: undefined,
            component: undefined,
        } as GenericSelectOption;
        
        setOptions([
            ...options.slice(0,-1), 
            newOption,
            options[options.length - 1]
        ]);
        setSelected(newOption);
        setValue(value);
        setShowNewChoice(false);

        // now add it to the DB for that custom property
        const newSettings = JSON.parse(customProperty.settings || "{}") as DropdownSettings;
        newSettings.choices = [...newSettings.choices, value];

        const payload = {
            id: customProperty.id,
            settings: JSON.stringify(newSettings),
        } as UpdateCustomPropertySettingsContract["Body"];

        const result = await apiUpdateCustomPropertySettings(payload);

        if (!result) {
            console.error("[AppCustomPropertyDropdown] Error updating custom property settings");
            toast("Error updating custom property settings", toastFail);
            return;
        }
            
        console.log("[AppCustomPropertyDropdown] Updated custom property settings: ", result);
        onChange(customProperty.label, value);
    }
    // const eventKeydDwnHandler = (event: KeyboardEvent) => {
        
    //     console.log("[AppCustomPropertyDropdown] key down: ", event.key);
    //     if (event.key === "Escape" && showNewChoice) {
    //         setShowNewChoice(false);
    //         setValue("");
    //     }
    // }

    const atLeastOneChecked = options.some((option) => option.value === value) || (showNewChoice && value);

    const requiredError = validate && customProperty.required && !atLeastOneChecked;
    // console.log("[AppCustomPropertyDropdown] options: ", options);
    // console.log("[AppCustomPropertyDropdown] selected: ", selected);
    // console.log("[AppCustomPropertyDropdown] settings: ", JSON.parse(customProperty.settings || "{}"));

    return (
        <div className="app-custom-property-dropdown">
            <div className="app-custom-property-value">
                {/* <div className="app-custom-property-label">
                    {customProperty.label}
                </div> */}
                {!showNewChoice && 
                    <>
                        <div className="app-custom-property-value">
                            <GenericSelect
                                options={options}
                                selected={selected}
                                disabled={disabled}
                                onChange={(value) => {
                                    if (value === "__new__") {
                                        setShowNewChoice(true);
                                        setValue("");
                                        return;
                                    }
                                    setValue(value);
                                    onChange(customProperty.label, value);
                                }}
                                maxHeight="200px"
                            />
                        </div>
                        <AppCustomPropertyError 
                            flagRequired={requiredError}
                        />          
                    </>

                }
                {showNewChoice &&
                    <div className="app-custom-property-dropdown-new-choice">
                        <input 
                            type="text"
                            autoFocus={true}
                            placeholder=""
                            value={value}
                            onChange={(event) => {
                                setValue(event.target.value);
                            }}
                            onKeyDown={(event) => {
                                console.log("[AppCustomPropertyDropdown] key down: ", event.key);
                                if (event.key === "Escape") {
                                    setShowNewChoice(false);
                                    setValue("");
                                    return;
                                }
                                if (event.key === "Enter") {
                                    if (value) {
                                        handleAddChoice(value);
                                    }
                                }
                            }}
                        />
                        <div className="app-custom-property-dropdown-new-choice-cancel" 
                            onClick={() => {
                                setShowNewChoice(false);
                                setValue(options.length > 0 ? options[0].value : "");
                            }}
                            style={requiredError ? {right: "24px"} : {}}
                        >
                            <X size={16} color="black"/>
                        </div>
                        <AppCustomPropertyError 
                            flagRequired={requiredError}
                        />                          
                    </div>
                }      
            </div>
               
        </div>
    )
}
